import { fetchPartnerWorkerLandingTemplates, type PartnerWorkerCustomTemplate } from '@/api/partner-workers';
import { Spinner } from '@/components/ui/spinner';
import LandingsEmptyState from '@/pages/landings/components/landings-empty-state';
import LandingsErrorState from '@/pages/landings/components/landings-error-state';
import { LayoutTemplateIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

interface TemplatesSubtabProps {
	workerProfileId: number;
}

export default function TemplatesSubtab({ workerProfileId }: TemplatesSubtabProps) {
	const [templates, setTemplates] = useState<PartnerWorkerCustomTemplate[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	const load = useCallback(async (showSpinner = false) => {
		if (showSpinner) setLoading(true);
		setError(false);
		try {
			const items = await fetchPartnerWorkerLandingTemplates(workerProfileId);
			setTemplates(items);
		} catch {
			setError(true);
		} finally {
			if (showSpinner) setLoading(false);
		}
	}, [workerProfileId]);

	useEffect(() => {
		void load(true);
	}, [load]);

	if (loading) {
		return (
			<div className='flex justify-center py-8'>
				<Spinner />
			</div>
		);
	}

	if (error) {
		return (
			<LandingsErrorState
				message='Не удалось загрузить шаблоны.'
				onRetry={() => void load(true)}
			/>
		);
	}

	if (templates.length === 0) {
		return (
			<LandingsEmptyState
				icon={LayoutTemplateIcon}
				title='Шаблонов нет'
				description='У воркера нет custom landing templates.'
			/>
		);
	}

	return (
		<div className='flex flex-col gap-2'>
			{templates.map(template => (
				<div key={template.id} className='rounded-xl border border-border bg-card px-3 py-3 text-sm'>
					<p className='font-medium'>{template.title}</p>
					<p className='text-xs text-muted-foreground mt-1'>
						{template.slug} · {template.entry}
					</p>
					<p className='text-xs text-muted-foreground mt-1'>
						{new Date(template.createdAt).toLocaleString('ru-RU')}
					</p>
				</div>
			))}
		</div>
	);
}
